import React, { useEffect, useState } from 'react';

import { appendAlineUrlParams, Select } from '@aline/common';
import { link } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { EditableNameVariant } from 'src/components/EditableNameInput';
import { PageHeader } from 'src/components/quick-start/PageHeader';
import { useGetWorkflowFiles } from 'src/hooks';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { WorkflowNameInput } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import {
  AddDocumentsStep,
  AddRecipientsStep,
  StepLayout,
  WorkflowCancelDialog,
} from './components';
import { ContentWrapper } from '../ContentWrapper';
import { workflowQuickStartSteps } from '../quick-start/components/helpers';

const useStyles = makeStyles()(theme => ({
  contentWrapper: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(3),
  },
  pageTitleContainer: {
    alignItems: 'flex-start',
    display: 'flex',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: 32,
  },
}));

export function SendForSignaturePage(): JSX.Element | null {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { updateActiveTabTitle } = useTabContext();
  const { workflowId } = useParams<{ workflowId: string }>();
  const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { workflowFiles, workflowWithFiles } = useGetWorkflowFiles(workflowId);
  const nextDisabledReason = !workflowWithFiles?.name
    ? 'Give your workflow a name'
    : !workflowFiles.length
      ? 'Add files to continue'
      : '';

  function goToPageTwo() {
    const firstWorkflowFile = workflowFiles.at(0);
    if (!firstWorkflowFile || !workflowId) return;

    navigate(
      appendAlineUrlParams(
        link.DocumentDetailAddInput({ documentId: firstWorkflowFile.document.id }),
        {
          s4sFlowParams: { s4sOn: true },
        }
      )
    );
  }

  useEffect(() => {
    updateActiveTabTitle(workflowWithFiles?.name || 'New Workflow');
  }, [updateActiveTabTitle, workflowWithFiles?.name]);

  if (!workflowId) return null;

  return (
    <>
      <Helmet>
        <title>New workflow</title>
      </Helmet>
      <div className={classes.root}>
        <PageHeader
          activeStep={1}
          cancelDataCy={Select.sendForSignature.actions.cancelButton}
          next={goToPageTwo}
          pageTitle="New workflow"
          primaryDataCy={Select.sendForSignature.actions.nextStepButton}
          primaryDisabledReason={nextDisabledReason}
          steps={workflowQuickStartSteps}
          onClose={() => setCancelDialogOpen(true)}
        />
        <ContentWrapper className={classes.contentWrapper}>
          <div className={classes.stepsList}>
            <StepLayout n={1} title="Add title">
              <WorkflowNameInput variant={EditableNameVariant.LargeText} workflowId={workflowId} />
            </StepLayout>
            <AddDocumentsStep workflowId={workflowId} />
            <AddRecipientsStep workflowId={workflowId} />
          </div>
        </ContentWrapper>
      </div>
      <WorkflowCancelDialog
        open={isCancelDialogOpen}
        setOpen={setCancelDialogOpen}
        workflowId={workflowId}
      />
    </>
  );
}
